import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useUserInvitations, useResendInvitation, useRevokeInvitation } from "@/hooks/useUserInvitations";
import { formatDistanceToNow } from "date-fns";
import { MailCheck, X, RefreshCw } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export function PendingInvitesTable() {
  const { data: invitations, isLoading } = useUserInvitations();
  const resendInvitation = useResendInvitation();
  const revokeInvitation = useRevokeInvitation();

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading invitations...</div>;
  }

  const pendingInvites = invitations?.filter(inv => inv.status === "pending") || [];

  if (pendingInvites.length === 0) {
    return <div className="text-center py-8 text-muted-foreground">No pending invitations.</div>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Email</TableHead>
          <TableHead>Roles</TableHead>
          <TableHead>Invited By</TableHead>
          <TableHead>Expires</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {pendingInvites.map((invitation) => {
          const isExpired = new Date(invitation.expires_at) < new Date();
          
          return (
            <TableRow key={invitation.id}>
              <TableCell className="font-medium">{invitation.email}</TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-1">
                {Array.isArray(invitation.roles) && invitation.roles.map((role: string) => (
                  <Badge key={role} variant="outline" className="capitalize">
                    {role.replace(/_/g, " ")}
                  </Badge>
                ))}
              </div>
            </TableCell>
              <TableCell className="text-muted-foreground">
                {invitation.invited_by?.display_name || invitation.invited_by?.email || "—"}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {formatDistanceToNow(new Date(invitation.expires_at), { addSuffix: true })}
              </TableCell>
              <TableCell>
                <Badge variant={isExpired ? "secondary" : "default"}>
                  {isExpired ? "Expired" : "Pending"}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => resendInvitation.mutate(invitation.id)}
                    disabled={resendInvitation.isPending}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <X className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Revoke invitation?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will cancel the invitation to {invitation.email}. They will no longer be able to use this invite link.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => revokeInvitation.mutate(invitation.id)}
                        >
                          Revoke
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
